import { EnvironmentProviders, Provider } from '@angular/core';
import {
    DIFFICULT_QUESTION_ELEMENT_ENET_TOKEN,
    DIFFICULT_QUESTION_ELEMENT_IS_MAIN_CLIENT,
    DIFFICULT_QUESTION_ELEMENT_LANGUAGE_CODE,
    DIFFICULT_QUESTION_ELEMENT_MUTE_AUDIO,
    ENVIRONMENT_TYPE,
    SLIDE_SYNC_SAS,
    VDO_SESSION,
} from 'shared/classroom/preview-screen/slide-rendering';
import { SLIDE_SYNC_TOOLBAR_CONFIG } from 'shared/classroom/toolbar/data-access';
import { SLIDE_CANVAS_TEXTBOARD_ICON_CONFIG } from 'shared/classroom/preview-screen/slide-rendering/ui';
import { SLIDE_SYNC_UI_CONFIG } from '../injection-tokens';
import { EnetTokenProvider, SasProvider, VdoTokenProvider } from '@pcp/auth';
import { of } from 'rxjs';

const provideVdoSessionFromProvider = (): Provider => ({
    provide: VDO_SESSION,
    useFactory: (provider: VdoTokenProvider) => provider.session$,
    deps: [VdoTokenProvider],
});

/** Required, not optional: `'vdo'` is a real slide type in every app that shares the renderer. */
const provideNoVdoSession = (): Provider => ({
    provide: VDO_SESSION,
    useValue: of(null),
});

/**
 * Shared across all client types.
 */
export const provideCommonConfig = (): (Provider | EnvironmentProviders)[] => [
    { provide: DIFFICULT_QUESTION_ELEMENT_LANGUAGE_CODE, useValue: 'DE' },
    {
        provide: DIFFICULT_QUESTION_ELEMENT_ENET_TOKEN,
        useFactory: (provider: EnetTokenProvider) => provider.enetToken$,
        deps: [EnetTokenProvider],
    },
    // Provide SAS using the AuthService.
    // Requires the user to include the PcpAuthModule.
    {
        provide: SLIDE_SYNC_SAS,
        deps: [SasProvider],
        useFactory: (provider: SasProvider) => provider.sas$,
    },
];

/**
 * Shared between pcp and tut teacher.
 */
export const provideTeacherConfig = (): (Provider | EnvironmentProviders)[] => [
    { provide: DIFFICULT_QUESTION_ELEMENT_MUTE_AUDIO, useValue: 'true' },
    {
        provide: DIFFICULT_QUESTION_ELEMENT_IS_MAIN_CLIENT,
        useValue: 'true',
    },
    provideVdoSessionFromProvider(),
];

/**
 * Shared between pcp and tut beamer.
 */
export const provideBeamerConfig = (): (Provider | EnvironmentProviders)[] => [
    { provide: DIFFICULT_QUESTION_ELEMENT_MUTE_AUDIO, useValue: 'false' },
    {
        provide: DIFFICULT_QUESTION_ELEMENT_IS_MAIN_CLIENT,
        useValue: 'true',
    },
    // Not hoisted into provideCommonConfig(): that would also enable the live-lesson apps.
    provideVdoSessionFromProvider(),
];

/**
 * For pcp live lesson moderator only.
 */
export const provideLiveLessonModeratorConfig = (): (Provider | EnvironmentProviders)[] => [
    { provide: DIFFICULT_QUESTION_ELEMENT_MUTE_AUDIO, useValue: 'true' },
    {
        provide: DIFFICULT_QUESTION_ELEMENT_IS_MAIN_CLIENT,
        useValue: 'false',
    },
    // Out of scope for #33254.
    provideNoVdoSession(),
];

/**
 * For pcp live lesson student only.
 */
export const provideLiveLessonStudentConfig = (): (Provider | EnvironmentProviders)[] => [
    { provide: DIFFICULT_QUESTION_ELEMENT_MUTE_AUDIO, useValue: 'false' },
    {
        provide: DIFFICULT_QUESTION_ELEMENT_IS_MAIN_CLIENT,
        useValue: 'false',
    },
    // Out of scope for #33254.
    provideNoVdoSession(),
];

/**
 * For pcp apps only.
 */
export const providePcpConfig = (): (Provider | EnvironmentProviders)[] => [
    {
        provide: SLIDE_SYNC_TOOLBAR_CONFIG,
        useValue: {
            colorPalette: [
                { color: '#808080' },
                { color: '#0038ff' },
                { color: '#F31818' },
                { color: '#FFFFFF' },
                { color: '#86BC25' },
                { color: '#FFC700' },
            ],
            colorDefault: '#F31818',
        },
    },
    // Canvas textboard paging-button + layout config (`canvas-textboard-slides`).
    {
        provide: SLIDE_CANVAS_TEXTBOARD_ICON_CONFIG,
        useValue: {
            pagingButtons: {
                normal: '',
                disabled: 'gray',
                hover: 'green',
            },
            layout: {
                // The 400 px right inset keeps wrapped text away from the paging-button
                // column, which lets the text area extend into the paging-button row at
                // the bottom — only safe because of the right inset.
                textOffsetRight: 400,
                textOffsetBottom: 30,
            },
        },
    },
    {
        provide: SLIDE_SYNC_UI_CONFIG,
        useValue: {
            addToFavoriteButton: {
                fontSet: 'material-symbols-outlined',
                fontIconChecked: 'favorite',
                fontIconUnchecked: 'favorite',
            },
        },
    },
    {
        provide: ENVIRONMENT_TYPE,
        useValue: 'PCP',
    },
];

/**
 * For tut apps only.
 */
export const provideTutorConfig = (): (Provider | EnvironmentProviders)[] => [
    {
        provide: SLIDE_SYNC_TOOLBAR_CONFIG,
        useValue: {
            colorPalette: [
                { color: '#DF0029' },
                { color: '#FFD200' },
                { color: '#77CC00' },
                { color: '#FFFFFF' },
                { color: '#0094FF' },
            ],
            colorDefault: '#0094FF',
        },
    },
    // Canvas textboard paging-button + layout config (`canvas-textboard-slides`).
    {
        provide: SLIDE_CANVAS_TEXTBOARD_ICON_CONFIG,
        useValue: {
            pagingButtons: {
                normal: 'brackground-gray',
                disabled: 'brackground-gray-gray',
                hover: 'brackground-white-blue',
            },
            layout: {
                // Text spans almost the full width — the bottom of the text area must stay
                // clear of the paging-button row (30 px slide padding + 70 px paging-button
                // reservation = 100).
                textOffsetRight: 20,
                textOffsetBottom: 100,
            },
        },
    },
    {
        provide: SLIDE_SYNC_UI_CONFIG,
        useValue: {
            addToFavoriteButton: {
                fontSet: 'fas',
                // note: using the same icon for checked and unchecked
                // CSS will differentiate between the states using the font color
                fontIconChecked: 'fa-heart',
                fontIconUnchecked: 'fa-heart',
            },
        },
    },
    {
        provide: ENVIRONMENT_TYPE,
        useValue: 'FahrTUTOR',
    },
];
